import { useId } from 'react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Chip, Field, Toggle } from '@/modules/automation/ui';
import type { AutomationParameter, ParameterValue } from '@/types/api';

/**
 * The choices a playbook or template offers, drawn from what the API says they are.
 *
 * The form only helps: every value is checked again by the server, which refuses anything outside
 * the parameter's options or pattern before a run exists.
 */
export function ParameterFields({
  parameters,
  values,
  onChange,
  errors,
  disabled = false,
}: {
  parameters: AutomationParameter[];
  values: Record<string, ParameterValue>;
  onChange: (key: string, value: ParameterValue) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}) {
  const baseId = useId();

  if (parameters.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing to choose: this runs the same way everywhere.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {parameters.map((parameter) => {
        const id = `${baseId}-${parameter.key}`;
        const value = values[parameter.key] ?? parameter.default;
        const error = errors[parameter.key];

        switch (parameter.type) {
          case 'boolean':
            return (
              <div key={parameter.key} className="md:col-span-2">
                <Toggle
                  id={id}
                  checked={value === true}
                  onChange={(checked) => onChange(parameter.key, checked)}
                  label={parameter.label}
                  description={parameter.help !== '' ? parameter.help : undefined}
                  disabled={disabled}
                />
                {error !== undefined && <p className="mt-1 text-xs text-danger">{error}</p>}
              </div>
            );

          case 'choice':
            return (
              <Field key={parameter.key} label={parameter.label} htmlFor={id} help={parameter.help} error={error}>
                <Select
                  id={id}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => onChange(parameter.key, event.target.value)}
                  disabled={disabled}
                >
                  {parameter.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            );

          case 'multi_choice': {
            const selected = Array.isArray(value) ? value : [];
            const toggle = (option: string) =>
              onChange(
                parameter.key,
                selected.includes(option) ? selected.filter((item) => item !== option) : [...selected, option],
              );

            return (
              <Field key={parameter.key} label={parameter.label} help={parameter.help} error={error} className="md:col-span-2">
                <div className="flex flex-wrap gap-1.5" role="group" aria-label={parameter.label}>
                  {parameter.options.map((option) => (
                    <Chip
                      key={option.value}
                      selected={selected.includes(option.value)}
                      onClick={() => toggle(option.value)}
                      disabled={disabled}
                    >
                      {option.label}
                    </Chip>
                  ))}
                </div>
              </Field>
            );
          }

          case 'list':
          case 'lines': {
            // Kept as the text the person typed while they type it — splitting on every keystroke
            // would swallow the new line they just pressed. The API splits it the same way: a list
            // on line breaks, spaces and commas, lines on line breaks only, because each one (an SSH
            // key) has spaces in it.
            const text = Array.isArray(value) ? value.join('\n') : typeof value === 'string' ? value : '';
            const how = parameter.type === 'lines' ? 'One per line.' : 'One per line, or separated by commas.';

            return (
              <Field
                key={parameter.key}
                label={parameter.label}
                htmlFor={id}
                help={parameter.help !== '' ? `${parameter.help} ${how}` : how}
                error={error}
                className="md:col-span-2"
              >
                <Textarea
                  id={id}
                  value={text}
                  onChange={(event) => onChange(parameter.key, event.target.value)}
                  className="min-h-20 font-mono text-[13px]"
                  spellCheck={false}
                  disabled={disabled}
                  aria-required={parameter.required}
                />
              </Field>
            );
          }

          case 'text':
          default:
            return (
              <Field key={parameter.key} label={parameter.label} htmlFor={id} help={parameter.help} error={error}>
                <Input
                  id={id}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => onChange(parameter.key, event.target.value)}
                  disabled={disabled}
                  aria-required={parameter.required}
                  spellCheck={false}
                />
              </Field>
            );
        }
      })}
    </div>
  );
}
